import axios from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';
import './App.css';
import { AuthModal } from './features/auth/AuthModal';
import { acceptTerms, claimReferral, createTelegramMigrationToken, getAuthMe, importTelegramAccount } from './features/auth/auth.api';
import { AuthMeResponse, AuthMode, AuthResponse, AuthSession, LinkedIdentity } from './features/auth/auth.schemas';
import { clearStoredSession, getStoredSession, storeSession } from './features/auth/auth.storage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { CabinetApp } from './features/cabinet/CabinetApp';
import { LandingPage } from './features/landing/LandingPage';

type AppMode = 'landing' | 'cabinet';

const initialSession = getStoredSession();

const getTelegramIdentity = (items?: LinkedIdentity[]) => (
  items?.find((identity) => {
    const source = `${identity.provider || ''} ${identity.type || ''} ${identity.identity_type || ''}`.toLowerCase();
    return source.includes('telegram')
      || Boolean(identity.provider_username || identity.telegram_username || identity.external_username || identity.metadata?.telegram_username);
  })
);

const getLinkedIdentityState = (data: AuthMeResponse | AuthResponse) => {
  const identities = 'identities' in data && data.identities ? data.identities : 'linked_identities' in data ? data.linked_identities : undefined;
  const telegramIdentity = getTelegramIdentity(identities as LinkedIdentity[] | undefined);
  const username = telegramIdentity?.provider_username
    || telegramIdentity?.telegram_username
    || telegramIdentity?.external_username
    || telegramIdentity?.metadata?.provider_username
    || telegramIdentity?.metadata?.telegram_username
    || telegramIdentity?.metadata?.username
    || telegramIdentity?.username
    || ('provider_username' in data ? data.provider_username : '')
    || ('telegram_username' in data ? data.telegram_username : '')
    || '';

  return {
    isLinked: Boolean(telegramIdentity || username),
    username,
  };
};

function App() {
  const [appPath, setAppPath] = useState(window.location.pathname);
  const [authMode, setAuthMode] = useState<AuthMode>('login');
  const [isAuthOpen, setIsAuthOpen] = useState(false);
  const [session, setSession] = useState<AuthSession | null>(initialSession);
  const [appMode, setAppMode] = useState<AppMode>(initialSession ? 'cabinet' : 'landing');
  const [isTelegramLinked, setIsTelegramLinked] = useState(false);
  const [telegramUsername, setTelegramUsername] = useState('');
  const migrationImportStartedRef = useRef(false);

  const isAuthenticated = Boolean(session);
  const isResetPasswordPath = appPath === '/reset-password';
  const resetPasswordToken = new URLSearchParams(window.location.search).get('token') || '';

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, [appMode]);

  const openAuth = (mode: AuthMode = 'login') => {
    setAuthMode(mode);
    setIsAuthOpen(true);
  };

  const navigateToLanding = () => {
    window.history.replaceState({}, '', '/');
    setAppPath('/');
    setAppMode('landing');
  };

  const openLoginFromReset = () => {
    navigateToLanding();
    setAuthMode('login');
    setIsAuthOpen(true);
  };

  const openCabinet = () => {
    if (!session) {
      openAuth('login');
      return;
    }

    setAppMode('cabinet');
  };

  const getReferralCode = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('ref') || params.get('start') || params.get('startapp') || '';
  };

  const getMigrationTokenFromUrl = () => {
    const params = new URLSearchParams(window.location.search);
    return params.get('migration_token') || params.get('migrationToken') || '';
  };

  const removeMigrationTokenFromUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('migration_token');
    url.searchParams.delete('migrationToken');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  };

  const getTelegramInitData = () => {
    const telegramWindow = window as Window & {
      Telegram?: {
        WebApp?: {
          initData?: string;
        };
      };
    };

    return telegramWindow.Telegram?.WebApp?.initData || '';
  };

  const getTelegramImportErrorMessage = (error: unknown) => {
    if (axios.isAxiosError(error)) {
      if (error.response?.status === 409) {
        return 'У аккаунта уже привязан другой Telegram';
      }

      if (error.response?.status === 401) {
        return 'Ссылка для связи Telegram устарела. Откройте ее заново.';
      }
    }

    return error instanceof Error ? error.message : 'Не удалось связать Telegram';
  };

  const importTelegramByToken = useCallback(async (migrationToken: string) => {
    if (!session) {
      openAuth('login');
      throw new Error('Сначала войдите на сайт');
    }

    const imported = await importTelegramAccount(migrationToken);
    const linkedState = getLinkedIdentityState(imported.data);
    const nextSession = {
      ...session,
      sessionToken: imported.sessionToken,
    };

    storeSession(nextSession);
    setSession(nextSession);
    setIsTelegramLinked(true);
    setTelegramUsername(linkedState.username);
    setAppMode('cabinet');
    window.dispatchEvent(new Event('mifvpn:refresh'));
    return 'Telegram аккаунт связан';
  }, [session]);

  const handleTelegramConnect = async () => {
    const initData = getTelegramInitData();

    if (!initData) {
      throw new Error('Откройте сайт через Telegram и попробуйте снова.');
    }

    try {
      const migration = await createTelegramMigrationToken(initData);
      return await importTelegramByToken(migration.token);
    } catch (error) {
      throw new Error(getTelegramImportErrorMessage(error));
    }
  };

  const handleAuthSuccess = async (nextSession: AuthSession) => {
    storeSession(nextSession);
    setSession(nextSession);
    setIsAuthOpen(false);

    if (authMode === 'register') {
      const referralCode = getReferralCode();

      if (referralCode) {
        await claimReferral(referralCode).catch(() => undefined);
      }

      await acceptTerms().catch(() => undefined);
    }

    window.dispatchEvent(new Event('mifvpn:refresh'));
    setAppMode('cabinet');
  };

  const handleLogout = () => {
    clearStoredSession();
    setSession(null);
    setIsTelegramLinked(false);
    setTelegramUsername('');
    setAppMode('landing');
  };

  useEffect(() => {
    const handleUnauthorized = () => {
      setSession(null);
      setAppMode('landing');
      setIsAuthOpen(true);
      setAuthMode('login');
    };

    window.addEventListener('mifvpn:auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('mifvpn:auth:unauthorized', handleUnauthorized);
  }, []);

  useEffect(() => {
    const handlePopState = () => {
      setAppPath(window.location.pathname);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  useEffect(() => {
    if (!session) {
      return;
    }

    getAuthMe()
      .then((me) => {
        const linkedState = getLinkedIdentityState(me);
        setIsTelegramLinked(linkedState.isLinked);
        setTelegramUsername(linkedState.username);

        if (me.email && me.email !== session.email) {
          const nextSession = { ...session, email: me.email };
          storeSession(nextSession);
          setSession(nextSession);
        }
      })
      .catch(() => undefined);
  }, [session]);

  useEffect(() => {
    const migrationToken = getMigrationTokenFromUrl();

    if (!session || !migrationToken || migrationImportStartedRef.current) {
      return;
    }

    migrationImportStartedRef.current = true;
    importTelegramByToken(migrationToken)
      .then(removeMigrationTokenFromUrl)
      .catch(() => {
        migrationImportStartedRef.current = false;
      });
  }, [importTelegramByToken, session]);

  if (isResetPasswordPath) {
    return (
      <ResetPasswordPage
        token={resetPasswordToken}
        onBackToLanding={navigateToLanding}
        onOpenLogin={openLoginFromReset}
      />
    );
  }

  if (appMode === 'cabinet') {
    return (
      <CabinetApp
        canConnectTelegram={Boolean(getTelegramInitData())}
        isTelegramLinked={isTelegramLinked}
        telegramUsername={telegramUsername}
        userEmail={session?.email}
        onBackToLanding={navigateToLanding}
        onConnectTelegram={handleTelegramConnect}
        onLogout={handleLogout}
      />
    );
  }

  return (
    <>
      <LandingPage
        isAuthenticated={isAuthenticated}
        session={session}
        onLogout={handleLogout}
        onOpenCabinet={openCabinet}
      />
      <AuthModal
        isOpen={isAuthOpen}
        mode={authMode}
        onClose={() => setIsAuthOpen(false)}
        onModeChange={setAuthMode}
        onSuccess={handleAuthSuccess}
      />
    </>
  );
}

export default App;
